import HomeOutlinedIcon from "@mui/icons-material/HomeOutlined";
import HelpOutlineOutlinedIcon from "@mui/icons-material/HelpOutlineOutlined";
import {
  Box,
  Button,
  IconButton,
  Menu,
  MenuItem,
  Typography,
  useTheme,
} from "@mui/material";
import {
  bindMenu,
  bindTrigger,
  usePopupState,
} from "material-ui-popup-state/hooks";
import { useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";

import { LoadZendeskWidget } from "./ZendeskWidget";
import { keepAwakeAvailable, openKeepAwake, shouldSkip } from "../app/wake";
import { getApiUrl } from "../config/product";
import { useGlobalConfig } from "../hooks/useGlobalConfig";
import { useHubLabel } from "../hub/hubLabel";
import { isConfigYes } from "../utils/config";
import {
  dropdownMenuListSx,
  navDropdownDividerSx,
  navDropdownHeaderSx,
  navDropdownHomeIconSx,
  navDropdownMenuItemSx,
  navDropdownMenuPaperSx,
  topBarIconButtonSx,
  topBarIconSx,
} from "../components/dropdownMenuStyles";

interface HelpMenuItem {
  key: string;
  /** Visible row text. The Hub row overrides this with the per-tenant hubLabel. */
  label: string;
  /**
   * ROUTER path — no "/app" prefix.
   *
   * These used to be `<Link href="/app/...">`, i.e. plain anchors, which is a
   * FULL DOCUMENT NAVIGATION: the SPA tears down and re-boots on every menu
   * click, re-running auth. That is also why the /app prefix was needed there
   * and must NOT appear here — react-router is mounted with basename "/app", so
   * navigate("/hub") resolves to /app/hub while navigate("/app/hub") would
   * resolve to /app/app/hub. The "Submit a Ticket" button in this same file
   * already used navigate(); these rows now match it.
   */
  path: string;
  /** Optional per-tenant gate. Absent = always shown. */
  enabled?: (flags: HelpMenuFlags) => boolean;
}

interface HelpMenuFlags {
  sopsEnabled: boolean;
  reportsAndFormsEnabled: boolean;
}

/**
 * THE label -> destination mapping, in menu order, as data.
 *
 * Previously each row was a hand-written <MenuItem><Link href=...> pair, so the
 * label and its destination were only ever associated by being adjacent in JSX
 * — seven near-identical 8-line blocks with nothing keeping a label and its
 * route together. One table means a row cannot silently drift from its page,
 * and the mapping can be asserted in a test rather than eyeballed.
 *
 * Grouped exactly as the dividers were: Aeronet docs | this tenant's surfaces.
 */
const HELP_MENU_GROUPS: HelpMenuItem[][] = [
  [
    { key: "roadmap", label: "Roadmap", path: "/aeronet-roadmap" },
    { key: "help", label: "Aeronet Help", path: "/aeronet-help" },
    { key: "release-notes", label: "Whats New", path: "/aeronet-release-notes" },
  ],
  [
    // Label is replaced at render time by the per-tenant hubLabel ("G2 Hub").
    { key: "hub", label: "Hub", path: "/hub", enabled: (f) => f.sopsEnabled },
    { key: "feature-requests", label: "Feature Requests", path: "/feature-requests" },
    { key: "tickets", label: "My Tickets", path: "/ticket-portal" },
    {
      key: "reports-and-forms",
      label: "Reports and Forms",
      path: "/reports-and-forms",
      enabled: (f) => f.reportsAndFormsEnabled,
    },
  ],
];

export const HELP_MENU_ITEMS: HelpMenuItem[] = HELP_MENU_GROUPS.flat();

/**
 * Paths that count as Help Center "active" (teal header when the menu is shut).
 *
 * DERIVED from the mapping above rather than written out, for two reasons.
 *
 * 1. It was WRONG, in a way that could not be seen by reading it. Every entry
 *    was "/app/..." — but react-router is mounted with basename "app"
 *    (src/index.tsx), and useLocation().pathname is basename-STRIPPED. On
 *    /app/hub the pathname is "/hub", so "/app/hub" could never match. Six of
 *    the seven prefixes were unsatisfiable and the header only ever went teal
 *    because the menu was open. That is the same never-true-predicate class as
 *    the `=== true` config gate documented below — a literal that looks
 *    plausible and is structurally unreachable.
 * 2. It had already drifted: "/aeronet-roadmap" was simply absent, so Roadmap
 *    would not have highlighted even with the prefix corrected.
 *
 * Deriving it means a destination cannot be added to the menu and forgotten
 * here, and the two lists cannot disagree about what "in the Help Center" means.
 */
export const HELP_ACTIVE_PATH_PREFIXES: string[] = HELP_MENU_GROUPS.flat().map(
  (item) => item.path
);

/** Teal CTA — spans menu width with small side inset (Help Center mock). */
const helpSubmitButtonSx = {
  display: "block",
  width: "100%",
  minWidth: 0,
  boxSizing: "border-box" as const,
  height: "34px",
  minHeight: "34px",
  padding: "6px 12px",
  fontSize: "13px",
  fontWeight: 600,
  textTransform: "uppercase" as const,
  letterSpacing: 0,
  lineHeight: 1.2,
  color: "white",
  backgroundColor: "#008e92",
  borderRadius: "4px",
  boxShadow: "none",
  "&:hover": {
    backgroundColor: "#018a90",
    boxShadow: "none",
  },
};

const HelpMenu = () => {
  const popupState = usePopupState({ variant: "popover", popupId: "helpMenu" });
  const navigate = useNavigate();
  const location = useLocation();
  const theme = useTheme();
  const globalConfig = useGlobalConfig();
  const hubLabel = useHubLabel();
  // Per-customer feature flags, now sourced from the `global` config function
  // (was `help_menu`) so React and Roxen gate off the same rows. Stored in Roxen
  // as config_function="global", config_variable="global_hub_enabled" /
  // "global_reports_and_forms_enabled", value "yes"/"no". The /v6/config
  // serializer strips the "global_" prefix (removeprefix), so the resolved keys
  // are `hub_enabled` / `reports_and_forms_enabled`.
  //
  // ⚠️ IT DOES **NOT** COERCE yes/no TO A BOOLEAN. This comment used to claim it
  // did, and that false belief WAS the bug: the gate was `=== true`, which no
  // config row can ever satisfy, so the Hub link never rendered on any tenant
  // that enabled it. g2's row is `hub_enabled = 'yes'` — the correct storage
  // form — and 'yes' === true is false, so the ONLY entry point to /app/hub was
  // unreachable. Measured across the whole g2 config: 3316 rows, value types
  // {str: 1756, int: 1137, dict: 309, list: 114}, rows returning a real boolean: 0.
  // app/config/schemas.py has three validators (http passthrough, dict,
  // comma-list) and none of them coerces yes/no.
  //
  // Config values are STRINGS — never coerce them, and never assume the server
  // did (memory reference_config_value_not_coerced; see the same warning verbatim
  // in src/bookings/tabs/BookingGeneral.tsx:29). ~15 sites in this codebase read
  // yes/no config correctly; the only four that did not were this pair and the
  // two in hub/hubLabel.ts — all four of them this one feature.
  //
  // isConfigYes accepts true/1/"yes"/"true"/"1", a strict SUPERSET of `=== true`,
  // so this cannot regress a tenant where the gate passes today — which matters
  // for the .nz prod installs, where the Hub does render.
  // Default OFF — only an explicit truthy flag reveals the entry.
  const sopsEnabled = isConfigYes(globalConfig?.hub_enabled);
  const reportsAndFormsEnabled = isConfigYes(
    globalConfig?.reports_and_forms_enabled
  );

  const path = location.pathname || "";
  const isHelpRouteActive = HELP_ACTIVE_PATH_PREFIXES.some(
    (prefix) => path === prefix || path.startsWith(`${prefix}/`)
  );
  const isHeaderHighlighted = popupState.isOpen || isHelpRouteActive;
  const headerTeal = theme.palette.primary.main;

  const flags: HelpMenuFlags = { sopsEnabled, reportsAndFormsEnabled };

  // "Keep awake…": the wake screen offers it only after a real cold start (src/app/wake.ts), so a warm tenant
  // would never see it. Asked once per page, on first open, and shown only where the tenant has keep-awake.
  const [keepAwakeOffered, setKeepAwakeOffered] = useState(false);
  useEffect(() => {
    if (!popupState.isOpen || shouldSkip(getApiUrl(), window.location.hostname, process.env.NODE_ENV)) return;
    let live = true;
    keepAwakeAvailable(getApiUrl()).then((ok) => live && setKeepAwakeOffered(ok));
    return () => {
      live = false;
    };
  }, [popupState.isOpen]);

  /** Close the menu, then navigate IN-APP (no full page reload). */
  const go = (path: string) => {
    popupState.close();
    navigate(path);
  };

  const handleKeepAwake = () => {
    popupState.close();
    openKeepAwake();
  };

  const handleSubmitTicket = () => {
    popupState.close();
    navigate("/ticket-portal?openCreate=true");
  };

  return (
    <LoadZendeskWidget>
      <IconButton
        aria-label="help"
        aria-controls="menu-help"
        color="inherit"
        sx={topBarIconButtonSx}
        {...bindTrigger(popupState)}
      >
        <HelpOutlineOutlinedIcon sx={topBarIconSx} />
      </IconButton>
      <Menu
        {...bindMenu(popupState)}
        anchorOrigin={{
          vertical: "bottom",
          horizontal: "center",
        }}
        transformOrigin={{
          vertical: "top",
          horizontal: "center",
        }}
        marginThreshold={8}
        // House tokens, UNMODIFIED — see src/app/Menu.tsx, which is the canonical
        // consumer. This menu used to override three of them and each override
        // was a visible inconsistency:
        //   px/pt/pb 1 on the Paper vs the house 1.5/1.5/2. The divider token
        //     insets itself by NAV_DROPDOWN_CONTENT_INSET (1.5), so with the
        //     panel padded to 1 the rules sat WIDER than the rows they divide —
        //     the "mismatched dividers".
        //   "& .MuiMenuItem-root": minHeight 36px vs the house 32px, so these
        //     rows were taller than every other dropdown in the app.
        //   display/flexDirection/alignItems, which dropdownMenuListSx already
        //     sets identically — dead weight that hid the two real overrides.
        PaperProps={{
          elevation: 0,
          sx: navDropdownMenuPaperSx,
        }}
        MenuListProps={{
          dense: true,
          disablePadding: true,
          // Don't auto-focus the "Help Center" header row (grey focus bg on open).
          autoFocusItem: false,
          sx: dropdownMenuListSx,
        }}
      >
        <MenuItem
          disableRipple
          disableTouchRipple
          aria-disabled
          tabIndex={-1}
          autoFocus={false}
          sx={[
            navDropdownHeaderSx,
            isHeaderHighlighted && {
              color: `${headerTeal} !important`,
              backgroundColor: "transparent !important",
              "&:hover, &:focus, &.Mui-focusVisible, &.Mui-selected": {
                backgroundColor: "transparent !important",
              },
            },
          ]}
        >
          <HomeOutlinedIcon
            sx={{
              ...navDropdownHomeIconSx,
              color: isHeaderHighlighted ? headerTeal : "inherit",
            }}
          />
          <Typography
            variant="inherit"
            sx={{
              color: isHeaderHighlighted
                ? `${headerTeal} !important`
                : "inherit",
            }}
          >
            Help Center
          </Typography>
        </MenuItem>
        <Box component="li" aria-hidden sx={navDropdownDividerSx} />

        {HELP_MENU_GROUPS.map((group, groupIndex) => [
          groupIndex > 0 ? (
            <Box
              key={`divider-${groupIndex}`}
              component="li"
              aria-hidden
              sx={navDropdownDividerSx}
            />
          ) : null,
          ...group
            .filter((item) => (item.enabled ? item.enabled(flags) : true))
            .map((item) => (
              <MenuItem
                key={item.key}
                onClick={() => go(item.path)}
                sx={navDropdownMenuItemSx}
              >
                {item.key === "hub" ? hubLabel : item.label}
              </MenuItem>
            )),
        ])}

        {keepAwakeOffered && [
          <Box key="divider-keep-awake" component="li" aria-hidden sx={navDropdownDividerSx} />,
          <MenuItem key="keep-awake" onClick={handleKeepAwake} sx={navDropdownMenuItemSx}>
            Keep awake…
          </MenuItem>,
        ]}

        <Box
          component="li"
          sx={{
            listStyle: "none",
            width: "100%",
            boxSizing: "border-box",
            px: 0,
            pt: 1,
            pb: 0.5,
          }}
        >
          <Button
            fullWidth
            variant="contained"
            disableElevation
            onClick={handleSubmitTicket}
            sx={helpSubmitButtonSx}
          >
            Submit a Ticket
          </Button>
        </Box>
      </Menu>
    </LoadZendeskWidget>
  );
};

export default HelpMenu;
